import { observer } from 'mobx-react';
import * as React from 'react';
import { CoreBatchUploadStore } from '../../stores/batch-upload';
import { BaseButton } from '../base/button';
import { BaseDialog } from '../base/dialog/dialog';
import { UploadProgressList } from './upload-progress-list';
import { getUploadProgressSummary } from './upload-utils';
import { useRateLimitCountdown } from './use-rate-limit-countdown';
import * as S from './batch-upload-widget.styles';

const BEFORE_UNLOAD_MESSAGE =
    'Document uploads are in progress. Leaving this page will cancel the uploads.';

export interface BatchUploadWidgetProps {
    batchUploadStore: CoreBatchUploadStore;
}

const BatchUploadWidgetComponent: React.FC<BatchUploadWidgetProps> = ({ batchUploadStore }) => {
    const [isDismissed, setIsDismissed] = React.useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);

    const { stats, isUploading, isPaused, isRateLimited, rateLimitResumeAt, uploadDelayReason, isActive } =
        batchUploadStore;
    const hasBatches = batchUploadStore.batches.length > 0;
    const rateLimitSecondsRemaining = useRateLimitCountdown(isRateLimited, rateLimitResumeAt);

    React.useEffect(() => {
        if (isActive || isUploading) {
            setIsDismissed(false);
        }
    }, [isActive, isUploading]);

    React.useEffect(() => {
        if (!isActive && !isUploading) {
            return undefined;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
            event.preventDefault();
            event.returnValue = BEFORE_UNLOAD_MESSAGE;
            return BEFORE_UNLOAD_MESSAGE;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isActive, isUploading]);

    if (!hasBatches || isDismissed) {
        return null;
    }

    const { title, subtitle } = getUploadProgressSummary(stats, isUploading, isPaused, {
        isRateLimited,
        rateLimitSecondsRemaining,
        rateLimitReason: uploadDelayReason,
    });
    const showDismiss = !isActive && !isUploading;
    const showPauseResume = isActive || isUploading;

    const handleDismiss = (): void => {
        setIsDismissed(true);
        setIsDetailsOpen(false);
        batchUploadStore.clearCompletedBatches();
    };

    const handleCancel = (): void => {
        batchUploadStore.cancel();
        setIsDetailsOpen(false);
    };

    return (
        <>
            <S.WidgetContainer role="status" aria-live="polite">
                <S.WidgetHeader>
                    <S.WidgetTitleGroup>
                        <S.WidgetTitle>{title}</S.WidgetTitle>
                        {subtitle && <S.WidgetSubtitle>{subtitle}</S.WidgetSubtitle>}
                    </S.WidgetTitleGroup>
                    {showDismiss && (
                        <BaseButton type="button" variant="text" size="small" onClick={handleDismiss}>
                            Dismiss
                        </BaseButton>
                    )}
                </S.WidgetHeader>

                <S.WidgetActions>
                    <BaseButton type="button" variant="outlined" size="small" onClick={() => setIsDetailsOpen(true)}>
                        Show details
                    </BaseButton>
                    {showPauseResume &&
                        (isPaused ? (
                            <BaseButton
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={() => batchUploadStore.resume()}>
                                Resume
                            </BaseButton>
                        ) : (
                            <BaseButton
                                type="button"
                                variant="outlined"
                                size="small"
                                onClick={() => batchUploadStore.pause()}
                                disabled={!isUploading && stats.pending === 0}>
                                Pause
                            </BaseButton>
                        ))}
                    {showPauseResume && (
                        <BaseButton type="button" variant="warning" size="small" onClick={handleCancel}>
                            Cancel
                        </BaseButton>
                    )}
                </S.WidgetActions>
            </S.WidgetContainer>

            <BaseDialog
                open={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                renderTitle="Upload details"
                showCloseButton={true}
                disableBackdropClick={isUploading}
                disableEscapeKeyDown={isUploading}>
                <S.DetailsBody>
                    <UploadProgressList
                        items={batchUploadStore.allItems}
                        stats={stats}
                        isUploading={isUploading}
                        isPaused={isPaused}
                        isRateLimited={isRateLimited}
                        rateLimitResumeAt={rateLimitResumeAt}
                        rateLimitReason={uploadDelayReason}
                    />
                </S.DetailsBody>
            </BaseDialog>
        </>
    );
};

export const BatchUploadWidget = observer(BatchUploadWidgetComponent);
